import { TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as Topics from "../Data/Topics";
import {Button} from "./Button";

export default class TimedButton extends Button {

    protected set percentage(percent: number) {
        this._percentage = percent;
        if (!this._locked) {
            this.drawMask(this._percentage);
        }
    }

    private _locked: boolean = false;
    private _percentage: number = 0;
    private readonly _duration: number;
    private readonly _cooldown: number;
    private readonly _overlayTexture: PIXI.Sprite = HLF.PixiUtils.makeSprite("fx-timer-bg", Constants.SPRITESHEET_UI);
    private readonly _overlayMask: PIXI.Graphics = new PIXI.Graphics();
    private _maskTween?: gsap.Animation;

    /**
     * A button that disables itself for a period of time after being clicked
     * @param pCallback The function to call on click
     * @param pAsset Image for the button
     * @param pSheet Spritesheet that the image is from
     * @param pCooldown How long (in milliseconds) to visually "recharge"
     * @param pDuration How long (in milliseconds) to stay disabled before "recharging"
     * @param pHoverVO Hover voiceover
     * @param pClickedSFX Click sound
     */
    constructor(pCallback: () => void,
                pAsset: string,
                pSheet: string,
                pDuration: number = 1000,
                pCooldown: number = 3000,
                pHoverVO?: string,
                pClickedSFX?: string,
    ) {
        super(pCallback, pAsset, pSheet, pHoverVO, pClickedSFX);
        this._duration = pDuration;
        this._cooldown = pCooldown;
        this._overlayTexture.mask = this._overlayMask;
        this._overlayTexture.addChild(this._overlayMask);
        this._visuals.addChild(this._overlayTexture);
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._maskTween?.kill();
        super.destroy(pOptions);
    }

    public set locked(pValue: boolean) {
        if (this._locked === pValue) {
            return;
        }
        this._locked = pValue;
        this._maskTween?.kill();
        if (pValue) {
            this.drawMask(1);
            this.scaleOver();
        }
        else {
            this.drawMask(0);
            this.scaleNormal();
        }
        this.interactive = !pValue;
        PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this);
    }

    public onFocusActivated(): void {
        super.onFocusActivated();
        if (this.interactive) {
            this.handleButtonClick();
        }
    }

    protected onPointerUp(pEvent: PIXI.interaction.InteractionEvent): void {
        if (this._eventData !== undefined && this._eventData.identifier === pEvent.data.identifier) {
            this.handleButtonClick();
        }
        super.onPointerUp(pEvent);
    }

    protected handleButtonClick() {
        this.interactive = false;
        PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this);
        const tweenMe = {value: 1};
        this.percentage = 1;
        this._maskTween =
            TweenLite
                .fromTo(tweenMe, this._cooldown / 1000, {value: 1}, {value: 0})
                .eventCallback(Constants.TWEEN_ONUPDATE, () => this.percentage = tweenMe.value)
                .eventCallback(Constants.TWEEN_ONCOMPLETE, () => {this.interactive = true; this.scaleNormal(); })
                .delay(this._duration / 1000)
            ;
    }

    protected drawMask(percent: number) {
        if (this._overlayMask === undefined) {
            return;
        }
        this._overlayMask.clear();
        const radius = Math.max(this._overlayTexture.width, this._overlayTexture.height) / 2;
        if (percent > 0 && percent < 1) {
            this._overlayMask.lineStyle(radius, 0xff0000, 1);
            this._overlayMask.arc(0, 0, radius / 2, 0, percent * PIXI.PI_2, false);
            this._overlayMask.rotation = -Math.PI / 2;
        }
        else if (percent >= 1) {
            this._overlayMask.beginFill(0xff0000, 1);
            this._overlayMask.drawCircle(0, 0, radius);
            this._overlayMask.endFill();
        }
    }

}
